import { ApolloError, useMutation } from '@apollo/client'
import { gql } from '__generated__'
import { Function, Match } from 'effect'
import { CQ } from 'libs/commandQuery/commandQuery'
import { CQReact } from 'libs/commandQuery/commandQuery.react'
import { ErrorLib, ProgramException, ReportableException } from 'libs/errors'
import { DisableMemberCommand, DisableMemberError, DisableMemberPayload } from 'presentation/screens/MembersScreen/MembersScreen.domain'
import { useCallback, useEffect, useRef } from 'react'

const MemberScreen_DisableMemberCommand = gql(/* GraphQL */ `
  mutation MemberScreen_DisableMemberCommand($id: ID!) {
    userDisable(id: $id) {
      user {
        id

        enterprise {
          ...EnterpriseMembers
        }
      }
      userErrors {
        message
      }
    }
  }
`)

/**
 * @NOTE Error management is weird because use case is weird -- we're looking to
 *   facade multiple commands including this under a single command
 */
export const useDisableMemberCommand = (): DisableMemberCommand => {
  const [disableMember, mutation] = useMutation(MemberScreen_DisableMemberCommand)
  const payloadRef = useRef<DisableMemberPayload | null>(null)
  const { state, transition } = CQReact.useStateManager<DisableMemberError, DisableMemberPayload>(CQ.createIdleState)
  const { id, regenerateId } = CQReact.useRegeneratableId()

  const execute = useCallback((payload: DisableMemberPayload) => {
    payloadRef.current = payload
    regenerateId()
    void disableMember({ variables: payload })
  }, [])

  useEffect(() => {
    Function.pipe(
      Match.value([payloadRef.current, mutation]),

      // Idle
      Match.when(
        [null, {}],
        () => transition.idle(),
      ),

      // Loading
      Match.when(
        [{}, { loading: true }],
        ([payload]) => transition.loading({ id, payload }),
      ),

      // Error
      Match.when(
        [{}, { error: Match.instanceOf(ApolloError) }],
        ([payload, mutation]) => {
          void ErrorLib.report(mutation.error)

          const error = new ProgramException('An error occurred while disabling a member')

          return transition.error({ id, error, payload })
        },
      ),

      // Success
      Match.when(
        [{}, { data: { userDisable: { user: {} } } }],
        ([payload]) => transition.success({ id, payload }),
      ),

      // Unexpected state
      Match.orElse(([payload, mutation]) => {
        void ErrorLib.report(new ReportableException('Unexpected state', {
          extraInfo: { payload, mutation },
        }))

        const error = new ProgramException('An error occurred while disabling a member')

        return transition.error({ id, error, payload: payload || FALLBACK_PAYLOAD })
      }),
    )
  }, [
    mutation.data,
    mutation.loading,
    mutation.error,
  ])

  return {
    execute,
    state,
  }
}

const FALLBACK_PAYLOAD = { id: '' }
